import * as React from 'react';
import { toast } from 'react-hot-toast';
import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/userContext/userContext';
import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
import { useStyle } from '../../context/styleContext/styleContext';

import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Drawer from '@mui/material/Drawer';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import Collapse from '@mui/material/Collapse';
import ListItemText from '@mui/material/ListItemText';

import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';

import logo from '../../images/LightIcon.svg';
import shoppingCartIcon from '../../images/shopping_cart.svg';

export default function Header() {
  const navigate = useNavigate();
  const { style } = useStyle();
  const { user, setUser } = useContext(UserContext);
  const { cartItems } = useContext(ShoppingCartContext);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);

  const moreOpen = Boolean(moreAnchorEl);

  const nav = (path) => {
    setMobileOpen(false);
    setMobileMoreOpen(false);
    setMoreAnchorEl(null);
    navigate(path);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setMobileOpen(false);
    setMobileMoreOpen(false);
    setMoreAnchorEl(null);
    toast.success('Logout successful');
  };

  const primaryLinks = [
    { label: 'Home', action: () => nav('/home') },
    { label: 'Plugins', action: () => nav('/plugins') },
    { label: 'Support', action: () => nav('/support') },
    { label: 'My Gear', action: () => nav('/my-gear') },
  ];

  const moreLinks = [
    { label: 'About', action: () => nav('/about') },
    { label: 'FAQ', action: () => nav('/faq') },
    { label: 'Learn', action: () => nav('/learning') },
    { label: 'Contact', action: () => nav('/contact') },
    { label: 'Privacy Policy', action: () => nav('/privacy-policy') },
    { label: 'Terms', action: () => nav('/terms') },
    { label: 'Return Policy', action: () => nav('/return-policy') },
  ];

  return (
    <Box sx={{ display: 'flex'}}>
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: style.color1,
          backgroundImage: 'none',
          minHeight: '60px',
          maxHeight: '60px',
          borderBottom: `1px solid ${style.color2}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: '60px !important' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Button
                onClick={() => nav('/home')}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 0,
                  borderRadius: '10px',
                  transition: 'filter 0.25s ease',
                  '&:hover': { filter: 'brightness(1.15)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ width: 42, height: 42, display: 'flex' }}>
                    <img
                      src={logo}
                      style={{ width: '100%', height: '100%' }}
                      alt="Parish Audio Logo"
                    />
                  </Box>
                  <Box
                    sx={{
                      fontSize: '1.12rem',
                      ml: 1,
                      color: style.color4,
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    Parish Audio
                  </Box>
                </Box>
              </Button>

              <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 3 }}>
                {primaryLinks.map((link) => (
                  <Button
                    key={link.label}
                    onClick={link.action}
                    sx={{
                      color: style.color4,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1.4,
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}

                <Button
                  onClick={(e) => setMoreAnchorEl(e.currentTarget)}
                  endIcon={<ExpandMoreRoundedIcon />}
                  sx={{
                    color: style.color4,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 1.4,
                    borderRadius: '10px',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  }}
                >
                  More
                </Button>

                <Menu
                  anchorEl={moreAnchorEl}
                  open={moreOpen}
                  onClose={() => setMoreAnchorEl(null)}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 220,
                      background: style.color1,
                      color: style.color4,
                      border: `1px solid ${style.color2}`,
                      borderRadius: '14px',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.32)',
                    },
                  }}
                >
                  {moreLinks.map((link) => (
                    <MenuItem
                      key={link.label}
                      onClick={link.action}
                      sx={{
                        py: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={link.label}
                        primaryTypographyProps={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 0.25,
                alignItems: 'center',
              }}
            >
              {user ? (
                <>
                  <Button
                    onClick={() => nav('/account')}
                    sx={{
                      color: style.color4,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                    }}
                  >
                    Account
                  </Button>
                  <Button
                    onClick={logout}
                    sx={{
                      color: style.color4,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                    }}
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => nav('/login')}
                    sx={{
                      color: style.color4,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                    }}
                  >
                    Sign in
                  </Button>
                  <Button
                    onClick={() => nav('/register')}
                    sx={{
                      color: style.color4,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '10px',
                    }}
                  >
                    Sign up
                  </Button>
                </>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
              <a href="/shopping-cart" style={{ display: 'flex', alignItems: 'center' }}>
                <Badge badgeContent={cartItems.length} color="primary" overlap="circular">
                  <img
                    src={shoppingCartIcon}
                    alt="Shopping cart"
                    style={{ width: '30px' }}
                  />
                </Badge>
              </a>
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}>
              <IconButton
                aria-label="Menu button"
                onClick={() => setMobileOpen(true)}
                sx={{
                  color: style.color4,
                  borderRadius: '10px',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                <MenuIcon sx={{ fontSize: '28px' }} />
              </IconButton>

              <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                PaperProps={{
                  sx: {
                    width: '86%',
                    maxWidth: 340,
                    background: style.color1,
                    color: style.color4,
                    borderLeft: `1px solid ${style.color2}`,
                    boxShadow: '-8px 0 30px rgba(0,0,0,0.35)',
                  },
                }}
              >
                <Box sx={{ p: 1.5, height: '100%' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ fontWeight: 700, fontSize: '1rem', color: style.color4 }}>
                      Menu
                    </Box>
                    <IconButton
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        color: style.color4,
                        borderRadius: '10px',
                      }}
                    >
                      <CloseRoundedIcon sx={{ fontSize: '24px' }} />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      borderRadius: '16px',
                      background: style.color1,
                      border: '1px solid rgba(255,255,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    {primaryLinks.map((link, index) => (
                      <React.Fragment key={link.label}>
                        <MenuItem
                          onClick={link.action}
                          sx={{
                            py: 1,
                            px: 1.5,
                            minHeight: 44,
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.05)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={link.label}
                            primaryTypographyProps={{
                              fontSize: '0.96rem',
                              fontWeight: 600,
                              color: style.color4,
                            }}
                          />
                        </MenuItem>
                        {index !== primaryLinks.length - 1 && (
                          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                        )}
                      </React.Fragment>
                    ))}

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                    <MenuItem
                      onClick={() => setMobileMoreOpen((prev) => !prev)}
                      sx={{
                        py: 1,
                        px: 1.5,
                        minHeight: 44,
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                      }}
                    >
                      <ListItemText
                        primary="More"
                        primaryTypographyProps={{
                          fontSize: '0.96rem',
                          fontWeight: 600,
                          color: style.color4,
                        }}
                      />
                      {mobileMoreOpen ? (
                        <ExpandLessRoundedIcon sx={{ color: style.color3 }} />
                      ) : (
                        <ChevronRightRoundedIcon sx={{ color: style.color3 }} />
                      )}
                    </MenuItem>

                    <Collapse in={mobileMoreOpen} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          background: style.darkPanel,
                          borderTop: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >
                        {moreLinks.map((link, index) => (
                          <React.Fragment key={link.label}>
                            <MenuItem
                              onClick={link.action}
                              sx={{
                                py: 0.9,
                                px: 2.25,
                                minHeight: 40,
                                '&:hover': {
                                  backgroundColor: 'rgba(255,255,255,0.04)',
                                },
                              }}
                            >
                              <ListItemText
                                primary={link.label}
                                primaryTypographyProps={{
                                  fontSize: '0.92rem',
                                  fontWeight: 500,
                                  color: style.color3,
                                }}
                              />
                            </MenuItem>
                            {index !== moreLinks.length - 1 && (
                              <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />
                            )}
                          </React.Fragment>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
      
                  <Box
                    sx={{
                      mt: 1.25,
                      borderRadius: '16px',
                      background: style.color1,
                      border: '1px solid rgba(255,255,255,0.08)',
                      overflow: 'hidden',
                    }}
                  >
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />
                    {!user ? (
                      <>
                        <MenuItem onClick={() => nav('/login')} sx={{ py: 1, px: 1.5, minHeight: 44 }}>
                          <ListItemText
                            primary="Sign In"
                            primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }}
                          />
                        </MenuItem>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                        <MenuItem onClick={() => nav('/register')} sx={{ py: 1, px: 1.5, minHeight: 44 }}>
                          <ListItemText
                            primary="Create Account"
                            primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }}
                          />
                        </MenuItem>
                      </>
                    ) : (
                      <>
                        <MenuItem onClick={() => nav('/account')} sx={{ py: 1, px: 1.5, minHeight: 44 }}>
                          <ListItemText
                            primary="Account"
                            primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }}
                          />
                        </MenuItem>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                        <MenuItem onClick={logout} sx={{ py: 1, px: 1.5, minHeight: 44 }}>
                          <ListItemText
                            primary="Log Out"
                            primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }}
                          />
                        </MenuItem>
                      </>
                    )}
                  </Box>
                </Box>
              </Drawer>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </Box>
  );
}

// import * as React from 'react';
// import { toast } from 'react-hot-toast';
// import { useState, useContext } from 'react';
// import { UserContext } from '../../context/userContext/userContext';
// import { useStyle } from '../../context/styleContext/styleContext';
// import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
// import { useNavigate } from 'react-router-dom';

// import Box from '@mui/material/Box';
// import AppBar from '@mui/material/AppBar';
// import Toolbar from '@mui/material/Toolbar';
// import Button from '@mui/material/Button';
// import IconButton from '@mui/material/IconButton';
// import Container from '@mui/material/Container';
// import Divider from '@mui/material/Divider';
// import MenuItem from '@mui/material/MenuItem';
// import Drawer from '@mui/material/Drawer';
// import Badge from '@mui/material/Badge';
// import Menu from '@mui/material/Menu';
// import ListItemText from '@mui/material/ListItemText';

// import MenuIcon from '@mui/icons-material/Menu';
// import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
// import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

// import logoIcon from '../../images/LightIcon.svg';
// import shoppingCartIcon from '../../images/shopping_cart.svg';

// export default function Header() {
//   const navigate = useNavigate();
//   const { style } = useStyle();
//   const { user, setUser } = useContext(UserContext);
//   const { cartItems } = useContext(ShoppingCartContext);

//   const [mobileOpen, setMobileOpen] = useState(false);
//   const [moreAnchorEl, setMoreAnchorEl] = useState(null);

//   const moreOpen = Boolean(moreAnchorEl);

//   const nav = (path) => {
//     setMobileOpen(false);
//     setMoreAnchorEl(null);
//     navigate(path);
//   };

//   const logout = () => {
//     localStorage.removeItem('token');
//     setUser(null);
//     setMobileOpen(false);
//     setMoreAnchorEl(null);
//     toast.success('Logout successful');
//   };

//   const openMoreMenu = (event) => {
//     setMoreAnchorEl(event.currentTarget);
//   };

//   const closeMoreMenu = () => {
//     setMoreAnchorEl(null);
//   };

//   const primaryLinks = [
//     { label: 'Home', action: () => nav('/home') },
//     { label: 'Plugins', action: () => nav('/plugins') },
//     { label: 'Support', action: () => nav('/support') },
//     { label: 'My Gear', action: () => nav('/my-gear') },
//   ];

//   const moreLinks = [
//     { label: 'About', action: () => nav('/about') },
//     { label: 'FAQ', action: () => nav('/faq') },
//     { label: 'Learn', action: () => nav('/learning') },
//     { label: 'Contact', action: () => nav('/contact') },
//     { label: 'Privacy Policy', action: () => nav('/privacy-policy') },
//     { label: 'Terms', action: () => nav('/terms') },
//     { label: 'Return Policy', action: () => nav('/return-policy') },
//   ];

//   return (
//     <Box sx={{ minHeight: '60px', display: 'flex' }}>
//       <AppBar
//         position="fixed"
//         enableColorOnDark
//         sx={{
//           boxShadow: 0,
//           bgcolor: style.color1,
//           backgroundImage: 'none',
//           minHeight: '60px',
//           maxHeight: '60px',
//           borderBottom: `1px solid ${style.color2}`,
//         }}
//       >
//         <Container maxWidth="lg">
//           <Toolbar disableGutters sx={{ minHeight: '60px !important' }}>
//             <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
//               <Button
//                 onClick={() => nav('/home')}
//                 sx={{
//                   textTransform: 'none',
//                   p: 0,
//                   minWidth: 0,
//                   transition: 'filter 0.25s ease',
//                   '&:hover': { filter: 'brightness(1.2)' },
//                 }}
//               >
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                   <Box sx={{ width: 46, height: 46, display: 'flex' }}>
//                     <img
//                       src={logoIcon}
//                       style={{ width: '100%', height: '100%' }}
//                       alt="Parish Audio Logo"
//                     />
//                   </Box>
//                   <Box
//                     sx={{
//                       fontSize: '1.2rem',
//                       ml: 1,
//                       color: style.color4,
//                       fontWeight: 700,
//                       letterSpacing: '-0.02em',
//                     }}
//                   >
//                     Parish Audio
//                   </Box>
//                 </Box>
//               </Button>

//               <Box sx={{ display: { xs: 'none', md: 'flex' }, ml: 3 }}>
//                 {primaryLinks.map((link) => (
//                   <Button
//                     key={link.label}
//                     variant="text"
//                     onClick={link.action}
//                     sx={{
//                       color: style.color4,
//                       textTransform: 'none',
//                       fontWeight: 600,
//                       px: 1.5,
//                       transition: 'filter 0.25s ease',
//                       '&:hover': { filter: 'brightness(1.35)' },
//                     }}
//                   >
//                     {link.label}
//                   </Button>
//                 ))}

//                 <Button
//                   variant="text"
//                   onClick={openMoreMenu}
//                   endIcon={<ExpandMoreRoundedIcon />}
//                   sx={{
//                     color: style.color4,
//                     textTransform: 'none',
//                     fontWeight: 600,
//                     px: 1.5,
//                     transition: 'filter 0.25s ease',
//                     '&:hover': { filter: 'brightness(1.35)' },
//                   }}
//                 >
//                   More
//                 </Button>

//                 <Menu
//                   anchorEl={moreAnchorEl}
//                   open={moreOpen}
//                   onClose={closeMoreMenu}
//                   PaperProps={{
//                     sx: {
//                       mt: 1,
//                       minWidth: 220,
//                       backgroundColor: style.color1,
//                       color: style.color4,
//                       border: `1px solid ${style.color2}`,
//                       boxShadow: '0 16px 40px rgba(0,0,0,0.28)',
//                     },
//                   }}
//                 >
//                   {moreLinks.map((link) => (
//                     <MenuItem
//                       key={link.label}
//                       onClick={link.action}
//                       sx={{
//                         py: 1.15,
//                         '&:hover': {
//                           backgroundColor: 'rgba(255,255,255,0.06)',
//                         },
//                       }}
//                     >
//                       <ListItemText
//                         primary={link.label}
//                         primaryTypographyProps={{
//                           fontSize: '0.95rem',
//                           fontWeight: 500,
//                         }}
//                       />
//                     </MenuItem>
//                   ))}
//                 </Menu>
//               </Box>
//             </Box>

//             <Box
//               sx={{
//                 display: { xs: 'none', md: 'flex' },
//                 gap: 0.5,
//                 alignItems: 'center',
//               }}
//             >
//               {user ? (
//                 <>
//                   <Button
//                     onClick={() => nav('/account')}
//                     sx={{
//                       color: style.color4,
//                       textTransform: 'none',
//                       fontWeight: 600,
//                       '&:hover': { filter: 'brightness(1.35)' },
//                     }}
//                   >
//                     Account
//                   </Button>
//                   <Button
//                     onClick={logout}
//                     sx={{
//                       color: style.color4,
//                       textTransform: 'none',
//                       fontWeight: 600,
//                       '&:hover': { filter: 'brightness(1.35)' },
//                     }}
//                   >
//                     Logout
//                   </Button>
//                 </>
//               ) : (
//                 <>
//                   <Button
//                     onClick={() => nav('/login')}
//                     sx={{
//                       color: style.color4,
//                       textTransform: 'none',
//                       fontWeight: 600,
//                       '&:hover': { filter: 'brightness(1.35)' },
//                     }}
//                   >
//                     Sign in
//                   </Button>
//                   <Button
//                     onClick={() => nav('/register')}
//                     sx={{
//                       color: style.color4,
//                       textTransform: 'none',
//                       fontWeight: 600,
//                       '&:hover': { filter: 'brightness(1.35)' },
//                     }}
//                   >
//                     Sign up
//                   </Button>
//                 </>
//               )}
//             </Box>

//             <Box sx={{ display: 'flex', alignItems: 'center', ml: 1.25 }}>
//               <a href="/shopping-cart" style={{ display: 'flex', alignItems: 'center' }}>
//                 <Badge
//                   badgeContent={cartItems.length}
//                   color="primary"
//                   overlap="circular"
//                   sx={{
//                     '&:hover': { filter: 'brightness(1.15)' },
//                     transition: 'filter 0.25s ease',
//                   }}
//                 >
//                   <img
//                     src={shoppingCartIcon}
//                     alt="Shopping cart"
//                     style={{ width: '30px' }}
//                   />
//                 </Badge>
//               </a>
//             </Box>

//             <Box sx={{ display: { xs: 'flex', md: 'none' }, ml: 1 }}>
//               <IconButton
//                 aria-label="Menu button"
//                 onClick={() => setMobileOpen(true)}
//                 sx={{ color: style.color4 }}
//               >
//                 <MenuIcon sx={{ fontSize: '28px' }} />
//               </IconButton>

//               <Drawer
//                 anchor="top"
//                 open={mobileOpen}
//                 onClose={() => setMobileOpen(false)}
//                 PaperProps={{
//                   sx: {
//                     backgroundColor: style.color4,
//                   },
//                 }}
//               >
//                 <Box sx={{ p: 2 }}>
//                   <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
//                     <IconButton onClick={() => setMobileOpen(false)}>
//                       <CloseRoundedIcon sx={{ fontSize: '28px' }} />
//                     </IconButton>
//                   </Box>

//                   {[...primaryLinks, ...moreLinks].map((link, index) => (
//                     <React.Fragment key={link.label}>
//                       <MenuItem onClick={link.action} sx={{ fontSize: '1.15rem', py: 1.25 }}>
//                         <strong>{link.label}</strong>
//                       </MenuItem>
//                       {index !== [...primaryLinks, ...moreLinks].length - 1 && (
//                         <Divider sx={{ my: 1.25 }} />
//                       )}
//                     </React.Fragment>
//                   ))}

//                   <Divider sx={{ my: 1.25 }} />

//                   {!user ? (
//                     <>
//                       <MenuItem onClick={() => nav('/register')} sx={{ fontSize: '1.15rem', py: 1.25 }}>
//                         <strong>Sign Up</strong>
//                       </MenuItem>
//                       <Divider sx={{ my: 1.25 }} />
//                       <MenuItem onClick={() => nav('/login')} sx={{ fontSize: '1.15rem', py: 1.25 }}>
//                         <strong>Log In</strong>
//                       </MenuItem>
//                     </>
//                   ) : (
//                     <>
//                       <MenuItem onClick={() => nav('/account')} sx={{ fontSize: '1.15rem', py: 1.25 }}>
//                         <strong>Account</strong>
//                       </MenuItem>
//                       <Divider sx={{ my: 1.25 }} />
//                       <MenuItem onClick={logout} sx={{ fontSize: '1.15rem', py: 1.25 }}>
//                         <strong>Log Out</strong>
//                       </MenuItem>
//                     </>
//                   )}
//                 </Box>
//               </Drawer>
//             </Box>
//           </Toolbar>
//         </Container>
//       </AppBar>
//     </Box>
//   );
// }

// import * as React from 'react';
// import {toast} from 'react-hot-toast'
// import {useState, useContext} from 'react';
// import{UserContext} from '../../context/userContext/userContext'
// import {useStyle} from '../../context/styleContext/styleContext'
// import { ShoppingCartContext } from '../../context/shoppingCartContext/shoppingCartContext';
// import { Link, useNavigate } from "react-router-dom";
// import { styled, alpha } from '@mui/material/styles';
// import Box from '@mui/material/Box';
// import AppBar from '@mui/material/AppBar';
// import Toolbar from '@mui/material/Toolbar';
// import Button from '@mui/material/Button';
// import IconButton from '@mui/material/IconButton';
// import Container from '@mui/material/Container';
// import Divider from '@mui/material/Divider';
// import MenuItem from '@mui/material/MenuItem';
// import Drawer from '@mui/material/Drawer';
// import MenuIcon from '@mui/icons-material/Menu';
// import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
// import Badge from '@mui/material/Badge';
// //import ColorModeIconDropdown from './ColorModeIconDropdown'
// //import Sitemark from './SitemarkIcon'
// import logoIcon from '../../images/LightIcon.svg'
// import shoppingCartIcon from '../../images/shopping_cart.svg'

// // const StyledToolbar = styled(Toolbar)(({ theme }) => ({
// //   display: 'flex',
// //   alignItems: 'center',
// //   justifyContent: 'space-between',
// //   flexShrink: 0,
// //   borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
// //   backdropFilter: 'blur(24px)',
// //   border: '1px solid',
// //   //borderColor: (theme.vars || theme).palette.divider,
// //   // backgroundColor: theme.vars
// //   //   ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
// //   //   : alpha(theme.palette.background.default, 0.4),
// //   //boxShadow: (theme.vars || theme).shadows[1],
// //   padding: '8px 12px',
// // }));

// export default function Header(){
//   const [open, setOpen] = React.useState(false);
//   // const toggleDrawer = (newOpen: boolean) => () => {
//   //   setOpen(newOpen);
//   // };
//   const navigate = useNavigate();
//   const [toggleDrawer, setToggleDrawer] = useState(false)
//   const { style, setStyle } = useStyle();
//   const { user, setUser, userEmail, userFirstName, userLastName } = useContext(UserContext);
//   const { cartItems, cartSubtotal } = useContext(ShoppingCartContext);
  
//   const navToHome = () => {
//     setOpen(false)
//     navigate('/home');
//   };
//   const navToMyGear = () => {
//     setOpen(false)
//     navigate('/my-gear');
//   };
//   const navToAdmin = () => {
//     setOpen(false)
//     navigate('/admin');
//   };
//   const navToTerms = () => {
//     navigate('/terms');
//   };

//   const navToPlugins = () => {
//     setOpen(false)
//     navigate('/plugins');
//   };

//   const navToSupport = () => {
//     setOpen(false)
//     navigate('/support');
//   };

//   const navToPrivacyPolicy = () => {
//     navigate('/privacy-policy');
//   };

//   const navToRegister = () => {
//     setOpen(false)
//     navigate('/register')
//   }

//   const navToLogin = () => {
//     setOpen(false)
//     navigate('/login')
//   }

//   const navToAccount = () => {
//     setOpen(false)
//     navigate('/account')
//   }

//   const logout = () => {
    
//     localStorage.removeItem('token')
//     setUser(null)
//     setOpen(false)
//     toast.success("Logout successful");
//     //navigate('/login')
//   }

//   const setDrawerToggleFalse = async (e) => {
//     e.preventDefault();
//     setOpen(false)
//     setToggleDrawer(false);
//     }

//   const setDrawerToggleTrue = async (e) => {
//     e.preventDefault();
//     setOpen(true)
//     setToggleDrawer(true);
//     }


//   return (
// <div style={{minHeight:'60px', display:'flex'}}>
//     <AppBar
//       position="fixed"
//       enableColorOnDark
//       sx={{
//         boxShadow: 0,
//         bgcolor: style.color1,
//         backgroundImage: 'none',
//         mt: 'calc(var(--template-frame-height, 0px))',
//         minHeight: '60px',
//         maxHeight: '60px',
//         flex:1
//       }}
//     >
//       <Container maxWidth="lg">
//         {/* <StyledToolbar variant="dense" disableGutters> */}
//         <Toolbar>
//           <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 0 }}>
//             {/* <Sitemark /> */}
//             <Button onClick={navToHome} sx={{textTransform:'none', marginLeft:'0px', transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(5)' } }}>
//             <div style={{display:'flex', flexDirection:'row'}}>
//             <div style={{ display:'flex', width:'50px', height:'50px', marginLeft:'0px'}}>
//                 <img src={logoIcon} style={{flex:1 }} alt="Parish Audio Logo - a stylized sound wave on a circular background"/>
//             </div>
//             <div style={{ fontSize:'1.3em', marginLeft:'5px', color:style.color4, alignContent:'center'}}>Parish Audio</div>
//             </div>
//             </Button>

//             <Box sx={{ display: { xs: 'none', md: 'flex', marginLeft:'30px' } }}>
//               <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToHome}>
//                 Home
//               </Button>
//               <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToPlugins}>
//                 Plugins
//               </Button>
//               <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToSupport}>
//                 Support
//               </Button>
//               <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToMyGear}>
//                   My Gear
//             </Button>
//               {user && user.isAdmin && (
//                 <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToAdmin}>
//                     Admin
//                 </Button>
//               )}
//             </Box>
//           </Box>
//           <Box
//             sx={{
//               display: { xs: 'none', md: 'flex' },
//               gap: 1,
//               alignItems: 'center',
//             }}
//           >


//         {user && (<>
//             <Button variant="text" sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToAccount}>
//               Account
//             </Button>
//             <Button variant="text"  sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={logout}>
//               Logout
//             </Button>


//           </>)}



//         {!user && (<>
//             <Button color={style.color4}  sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToLogin}>
//               Sign in
//             </Button>
//             <Button color={style.color4} sx={{color:style.color4, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(10)' }}} size="medium" onClick={navToRegister}>
//               Sign up
//             </Button>


//           </>)}






//             {/* <Button color="primary" variant="text" size="small" onClick={navToLogin}>
//               Sign in
//             </Button>
//             <Button color="primary" variant="contained" size="small" onClick={navToRegister}>
//               Sign up
//             </Button> */}
//             {/* <ColorModeIconDropdown /> */}
//           </Box>
//           <Box sx={{ display: { xs: 'flex', md: 'none'}, gap: 1 }}>
//             {/* <ColorModeIconDropdown size="medium" /> */}
//             <IconButton aria-label="Menu button" onClick={setDrawerToggleTrue} sx={{color:style.color4, size:'large', minWidth:'75px'}}>
//               <MenuIcon sx={{size:'large', color:style.color4, fontSize:'24pt'}} />
//             </IconButton>
//             <Drawer
//               anchor="top"
//               open={open}
//               onClose={setDrawerToggleFalse}
//               PaperProps={{
//                 sx: {
//                   top: 'var(--template-frame-height, 0px)'
//                 },
//               }}
//             >
//               <Box sx={{ p: 2, backgroundColor: style.color4 }}>
//                 <Box
//                   sx={{
//                     display: 'flex',
//                     justifyContent: 'flex-end',
//                   }}
//                 >
//                   <IconButton onClick={setDrawerToggleFalse}>
//                     <CloseRoundedIcon sx={{fontSize:'24pt'}}/>
//                   </IconButton>
//                 </Box>

//                 <MenuItem onClick={navToHome} sx={{fontSize:'1.3em'}}><strong>Home</strong></MenuItem>
//                 <Divider sx={{ my: 3 }} />
//                 <MenuItem onClick={navToPlugins} sx={{fontSize:'1.3em'}}><strong>Plugins</strong></MenuItem>
//                 <Divider sx={{ my: 3 }} />
//                 <MenuItem onClick={navToSupport} sx={{fontSize:'1.3em'}}><strong>Support</strong></MenuItem>
//                 <Divider sx={{ my: 3 }} />
//                 <MenuItem onClick={navToMyGear} sx={{fontSize:'1.3em'}}><strong>My Gear</strong></MenuItem>
//                 <Divider sx={{ my: 3 }} />
//                 {!user &&(<>
//                     <MenuItem onClick={navToRegister} sx={{fontSize:'1.3em'}}><strong>Sign Up</strong></MenuItem>
//                       <Divider sx={{ my: 3 }} />
//                     <MenuItem onClick={navToLogin} sx={{fontSize:'1.3em'}}><strong>Log In</strong></MenuItem>
//                   </>
//                 )}
//                 {user &&(<>
//                   <MenuItem onClick={navToAccount} sx={{fontSize:'1.3em'}}><strong>Account</strong></MenuItem>
//                       <Divider sx={{ my: 3 }} />
//                     <MenuItem onClick={logout} sx={{fontSize:'1.3em'}}><strong>Log Out</strong></MenuItem>
                
//                 </>)}

//               </Box>
//             </Drawer>
//           </Box>
//           <div style={{maxWidth:'60px'}}>
//             <a href='/shopping-cart'>
//             <Badge badgeContent={cartItems.length} color='primary' overlap="circular" sx={{ transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.8)' }}}>
//               <img src={shoppingCartIcon} alt="Light blue Shopping Cart icon" style={{width:'30px'}}/>
//               </Badge>

//             </a>
//           </div>
//         {/* </StyledToolbar> */}
//         </Toolbar>
//       </Container>
//     </AppBar>
//     </div>
//   );
// }